import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    TextInput,
    Textarea,
    Select,
    Switch,
    Badge,
    Text,
    Title,
    Divider,
    Button,
    SimpleGrid,
    Modal,
    Table,
    Paper,
    Alert,
    Group,
    Stack,
    UnstyledButton
} from '@mantine/core';
import {
    IconPlus,
    IconSearch,
    IconEye,
    IconDice,
    IconDatabase,
    IconX,
    IconInfoCircle
} from '@tabler/icons-react';
import { useEntityTypes } from '../../contexts/EntityTypesContext';
import { useSchemaEntityTypes } from '../../hooks/useSchemaEntityTypes';
import { EntityBrowserDialog } from '../dialogs';
import { entityService } from '../../services/entityServiceSingleton';
import CentralizedSchemaService from '../../services/CentralizedSchemaService';
import { extractErrorMessage } from '../../utils/apiUtils';
import { LoadingState } from './LoadingState';

export interface SetFormData {
    displayName: string;
    description: string;
    filterScope: string;
    type: 'manual' | 'enumerated';
    isSystem: boolean;
    manualMembers: Array<string | { Id: string; DisplayName?: string; EntityType?: string }>;
    filter: string;
}

interface SetFormProps {
    data: SetFormData;
    onChange: (data: SetFormData) => void;
    disabled?: boolean;
    showSystemToggle?: boolean;
    showDescription?: boolean;
    showPreviewMembers?: boolean;
    /** Standard mode hides admin tools (Generate Random Filter, View Attributes). */
    isStandard?: boolean;
}

const SetForm: React.FC<SetFormProps> = ({
    data,
    onChange,
    disabled = false,
    showSystemToggle = true,
    showDescription = true,
    showPreviewMembers = false,
    isStandard = false
}) => {
    const [isDynamicMode, setIsDynamicMode] = useState(data.type === 'enumerated');
    const [newManualId, setNewManualId] = useState('');
    const [entityBrowserOpen, setEntityBrowserOpen] = useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [previewTotalResults, setPreviewTotalResults] = useState<number | null>(null);
    const [showAttributesDialog, setShowAttributesDialog] = useState(false);
    const [availableAttributes, setAvailableAttributes] = useState<any[]>([]);
    const { entityTypes: contextEntityTypes } = useEntityTypes();
    const { entityTypes: schemaEntityTypes } = useSchemaEntityTypes();

    // State to store display names for manual set members
    const [memberDisplayNames, setMemberDisplayNames] = useState<Record<string, string>>({});
    const [memberNamesLoading, setMemberNamesLoading] = useState(false);
    const [memberWarnings, setMemberWarnings] = useState<Record<string, string>>({});
    
    // State to store full entity objects for already-selected members (for EntityBrowserDialog)
    const [alreadySelectedEntities, setAlreadySelectedEntities] = useState<any[]>([]);

    const getMemberId = (member: unknown): string => {
        if (member == null) return '';
        if (typeof member === 'string') return member;
        if (typeof member === 'object') {
            const m = member as any;
            return (m.Id ?? m.id ?? m.$ref ?? '').toString();
        }
        return '';
    };

    const getMemberEntityType = (member: unknown): string => {
        if (member == null || typeof member !== 'object') return '';
        const m = member as any;
        return (m.EntityType ?? m.entityType ?? '').toString().trim();
    };

    // Normalize filterScope to match entity type name format (case-insensitive matching)
    const normalizedFilterScope = useMemo(() => {
        if (!data.filterScope) return '';
        const types = schemaEntityTypes.length > 0 ? schemaEntityTypes : contextEntityTypes;
        const matchingType = types.find((type: { name: string }) =>
            type.name.toLowerCase() === data.filterScope.toLowerCase()
        );
        return matchingType ? matchingType.name : data.filterScope;
    }, [data.filterScope, schemaEntityTypes, contextEntityTypes]);

    // Entity type options for Select
    const entityTypeOptions = useMemo(() => {
        const types = schemaEntityTypes.length > 0 ? schemaEntityTypes : contextEntityTypes;
        return types.map((entityType: { name: string }) => ({
            value: entityType.name,
            label: entityType.name
        }));
    }, [schemaEntityTypes, contextEntityTypes]);

    // Sample SCIM filter expressions for different entity types
    const sampleFilters = [
        'active eq true',
        'active eq false',
        'userName pr',
        'emails.value pr',
        'name.familyName pr',
        'meta.lastModified pr',
        'meta.created pr',
        'displayName pr',
        'userType eq "Employee"',
        'userType eq "Student"',
        'userType eq "Contractor"',
        'title pr',
        'department pr',
        'manager pr',
        'employeeNumber pr',
        'costCenter pr',
        'division pr',
        'organization pr',
        'addresses.type eq "work"',
        'phoneNumbers.type eq "work"',
        'emails.type eq "work"',
        'roles.value eq "Administrator"',
        'roles.value eq "User"',
        'groups.display eq "Students"',
        'groups.display eq "Faculty"',
        'entitlements.value eq "read"',
        'entitlements.value eq "write"',
        'x509Certificates pr',
        'photos pr',
        'locale eq "en_US"',
        'timezone eq "America/New_York"',
        'preferredLanguage eq "en"',
        'nickName pr',
        'profileUrl pr',
        'ims pr',
        'externalId pr',
        'meta.resourceType eq "User"',
        'meta.location pr',
        'meta.version pr'
    ];

    // Generate random filter expression based on entity type
    const generateRandomFilter = async () => {
        if (!data.filterScope) {
            console.warn('Cannot generate filter without filterScope');
            return;
        }

        try {
            const centralizedService = CentralizedSchemaService.getInstance();
            const attributes = await centralizedService.getFieldOptionsForEntityType(data.filterScope);

            if (!attributes || attributes.length === 0) {
                // Fallback to generic filters if no attributes found
                const randomFilter = sampleFilters[Math.floor(Math.random() * sampleFilters.length)];
                handleFieldChange('filter', randomFilter);
                return;
            }

            // Filter out system attributes and get usable ones
            const usableAttributes = attributes.filter(attr =>
                attr.name &&
                !attr.name.startsWith('meta.') &&
                !attr.name.startsWith('schemas') &&
                attr.name !== 'id' &&
                attr.name !== 'externalId'
            );

            if (usableAttributes.length === 0) {
                // Fallback if no usable attributes
                const randomFilter = sampleFilters[Math.floor(Math.random() * sampleFilters.length)];
                handleFieldChange('filter', randomFilter);
                return;
            }

            // Pick a random attribute
            const randomAttr = usableAttributes[Math.floor(Math.random() * usableAttributes.length)];
            const attrName = randomAttr.name;

            // Generate appropriate filter based on attribute type
            let filterExpression = '';

            if (randomAttr.type === 'boolean') {
                filterExpression = `${attrName} eq true`;
            } else if (randomAttr.type === 'integer' || randomAttr.type === 'decimal') {
                const operators = ['eq', 'ne', 'gt', 'lt', 'ge', 'le'];
                const operator = operators[Math.floor(Math.random() * operators.length)];
                const value = randomAttr.type === 'integer' ?
                    Math.floor(Math.random() * 100) :
                    (Math.random() * 100).toFixed(2);
                filterExpression = `${attrName} ${operator} ${value}`;
            } else if (randomAttr.type === 'dateTime' || randomAttr.type === 'date') {
                // For DateTime/Date attributes, use date operators with ISO 8601 format
                const operators = ['pr', 'eq', 'ne', 'gt', 'lt', 'ge', 'le'];
                const operator = operators[Math.floor(Math.random() * operators.length)];

                if (operator === 'pr') {
                    filterExpression = `${attrName} pr`;
                } else {
                    // Generate a random date within the last year
                    const now = new Date();
                    const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
                    const randomDate = new Date(oneYearAgo.getTime() + Math.random() * (now.getTime() - oneYearAgo.getTime()));
                    // Format as "2017-04-24T00:00:00" in local time (API's JSON serializer format)
                    const year = randomDate.getFullYear();
                    const month = String(randomDate.getMonth() + 1).padStart(2, '0');
                    const day = String(randomDate.getDate()).padStart(2, '0');
                    const hours = String(randomDate.getHours()).padStart(2, '0');
                    const minutes = String(randomDate.getMinutes()).padStart(2, '0');
                    const seconds = String(randomDate.getSeconds()).padStart(2, '0');
                    const apiDate = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
                    filterExpression = `${attrName} ${operator} "${apiDate}"`;
                }
            } else if (randomAttr.multiValued) {
                // For multi-valued attributes, use 'pr' (present) or 'co' (contains) with a value
                if (Math.random() > 0.5) {
                    filterExpression = `${attrName} pr`;
                } else {
                    const sampleValues = ['Active', 'Enabled', 'Valid', 'Available', 'Open'];
                    const value = sampleValues[Math.floor(Math.random() * sampleValues.length)];
                    filterExpression = `${attrName} co "${value}"`;
                }
            } else {
                // For string attributes, use 'pr' (present) or 'eq' with a value
                if (Math.random() > 0.5) {
                    filterExpression = `${attrName} pr`;
                } else {
                    const sampleValues = ['Active', 'Enabled', 'Valid', 'Available', 'Open', 'Test'];
                    const value = sampleValues[Math.floor(Math.random() * sampleValues.length)];
                    filterExpression = `${attrName} eq "${value}"`;
                }
            }

            handleFieldChange('filter', filterExpression);
        } catch (err) {
            console.error('Error generating entity-specific filter:', err);
            // Fallback to generic filters
            const randomFilter = sampleFilters[Math.floor(Math.random() * sampleFilters.length)];
            handleFieldChange('filter', randomFilter);
        }
    };

    // Load available attributes for the current entity type
    const loadAvailableAttributes = async () => {
        if (!data.filterScope) return;

        try {
            const centralizedService = CentralizedSchemaService.getInstance();
            const attributes = await centralizedService.getFieldOptionsForEntityType(data.filterScope);
            setAvailableAttributes(attributes);
            setShowAttributesDialog(true);
        } catch (err) {
            console.error('Error loading attributes:', err);
        }
    };

    // Update form data when props change
    useEffect(() => {
        setIsDynamicMode(data.type === 'enumerated');
    }, [data.type]);

    // Fetch display names for manual set members
    useEffect(() => {
        const fetchMemberDisplayNames = async () => {
            if (!data.filterScope || data.manualMembers.length === 0 || data.type !== 'manual') {
                setMemberDisplayNames({});
                setMemberWarnings({});
                return;
            }

            setMemberNamesLoading(true);
            const displayNames: Record<string, string> = {};
            const warnings: Record<string, string> = {};

            try {
                // Fetch display names for all members in parallel
                const fetchPromises = data.manualMembers.map(async (member) => {
                    const memberId = getMemberId(member);
                    if (!memberId) return;
                    try {
                        const explicitType = getMemberEntityType(member);
                        const fetchType = (explicitType || data.filterScope).toString().trim();
                        if (explicitType && normalizedFilterScope && explicitType.toLowerCase() !== normalizedFilterScope.toLowerCase()) {
                            warnings[memberId] = `Member type "${explicitType}" does not match FilterScope "${normalizedFilterScope}".`;
                        }

                        if (typeof member === 'object' && member != null) {
                            const m = member as any;
                            const preName = (m.DisplayName ?? m.displayName ?? m.Name ?? m.name ?? '').toString().trim();
                            if (preName) {
                                displayNames[memberId] = preName;
                                return;
                            }
                        }

                        const entity = await entityService.getEntityById(memberId, fetchType.toLowerCase(), true);
                        const displayName = entity?.DisplayName || entity?.displayName || entity?.Name || entity?.name || memberId;
                        displayNames[memberId] = displayName;
                    } catch (error) {
                        console.error(`Failed to fetch display name for member ${memberId}:`, error);
                        displayNames[memberId] = memberId;
                        // If we can't fetch it under the filter scope, warn (likely wrong type or missing entity)
                        warnings[memberId] = warnings[memberId] || `Could not resolve this member under FilterScope "${normalizedFilterScope || data.filterScope}".`;
                    }
                });

                await Promise.all(fetchPromises);
                setMemberDisplayNames(displayNames);
                setMemberWarnings(warnings);
            } catch (error) {
                console.error('Error fetching member display names:', error);
                // Fallback: use IDs as display names
                const fallbackNames: Record<string, string> = {};
                data.manualMembers.forEach(m => {
                    const id = getMemberId(m);
                    if (id) fallbackNames[id] = id;
                });
                setMemberDisplayNames(fallbackNames);
                setMemberWarnings({});
            } finally {
                setMemberNamesLoading(false);
            }
        };

        fetchMemberDisplayNames();
    }, [data.manualMembers, data.filterScope, data.type, entityService, normalizedFilterScope]);

    // Fetch full entity objects for already-selected members (for EntityBrowserDialog)
    useEffect(() => {
        const fetchAlreadySelectedEntities = async () => {
            if (!data.filterScope || data.manualMembers.length === 0 || data.type !== 'manual') {
                setAlreadySelectedEntities([]);
                return;
            }

            try {
                // Fetch full entities for all manual members in parallel
                const fetchPromises = data.manualMembers.map(async (member) => {
                    const memberId = getMemberId(member);
                    if (!memberId) return null;
                    try {
                        const explicitType = getMemberEntityType(member);
                        const fetchType = (explicitType || data.filterScope).toString().trim();
                        const entity = await entityService.getEntityById(memberId, fetchType.toLowerCase(), true);
                        return entity;
                    } catch (error) {
                        console.error(`Failed to fetch entity for member ${memberId}:`, error);
                        // Return a minimal entity object with just the ID
                        return { Id: memberId, DisplayName: memberId };
                    }
                });

                const entities = await Promise.all(fetchPromises);
                setAlreadySelectedEntities(entities.filter(e => e !== null && e !== undefined));
            } catch (error) {
                console.error('Error fetching already-selected entities:', error);
                setAlreadySelectedEntities([]);
            }
        };

        fetchAlreadySelectedEntities();
    }, [data.manualMembers, data.filterScope, data.type, entityService]);

    const handleFieldChange = (field: keyof SetFormData, value: any) => {
        onChange({
            ...data,
            [field]: value
        });
    };

    const handleModeChange = (isDynamic: boolean) => {
        setIsDynamicMode(isDynamic);
        const newType = isDynamic ? 'enumerated' : 'manual';
        onChange({
            ...data,
            type: newType,
            // Clear mode-specific data when switching
            manualMembers: isDynamic ? [] : data.manualMembers,
            filter: isDynamic ? data.filter : ''
        });
    };

    const handleAddManualId = () => {
        const next = newManualId.trim();
        if (!next) return;
        const existingIds = new Set(data.manualMembers.map(getMemberId).filter(Boolean));
        if (!existingIds.has(next)) {
            const newMembers = [...data.manualMembers, next];
            handleFieldChange('manualMembers', newMembers);
            setNewManualId('');
        }
    };

    const handleRemoveManualId = (idToRemove: string) => {
        const newMembers = data.manualMembers.filter(m => getMemberId(m) !== idToRemove);
        handleFieldChange('manualMembers', newMembers);
    };

    const handleEntityBrowserConfirm = (selectedEntities: Array<{ Id: string; DisplayName: string; EntityType: string }>) => {
        const existingIds = new Set(data.manualMembers.map(getMemberId).filter(Boolean));
        const toAdd = selectedEntities
            .filter((e) => e?.Id && !existingIds.has(e.Id))
            .map((e) => ({ Id: e.Id, DisplayName: e.DisplayName, EntityType: e.EntityType }));
        const newMembers = [...data.manualMembers, ...toAdd];
        handleFieldChange('manualMembers', newMembers);
        setEntityBrowserOpen(false);
    };

    const getPluralizedLabel = (entityType: string) => {
        if (entityType.endsWith('y')) {
            return entityType.slice(0, -1) + 'ies';
        } else if (entityType.endsWith('s') || entityType.endsWith('sh') || entityType.endsWith('ch')) {
            return entityType + 'es';
        } else {
            return entityType + 's';
        }
    };

    const handlePreviewMembers = async () => {
        if (!data.filterScope) {
            setPreviewError('Please select a filter scope first');
            return;
        }

        setPreviewLoading(true);
        setPreviewError(null);
        setPreviewTotalResults(null);
        setPreviewDialogOpen(true);

        try {
            let entities: any[] = [];


            if (data.type === 'manual') {
                // For manual sets, show ONLY the specified members
                if (data.manualMembers.length === 0) {
                    setPreviewData([]);
                    setPreviewLoading(false);
                    return;
                }


                // Fetch details for each manual member ONLY
                const memberPromises = data.manualMembers.map(async (member) => {
                    const memberId = getMemberId(member);
                    const explicitType = getMemberEntityType(member);
                    const fetchType = (explicitType || data.filterScope).toString().trim();
                    try {
                        return await entityService.getEntityById(memberId, fetchType);
                    } catch (err: any) {
                        console.warn(`Failed to fetch member ${memberId}:`, err);
                        const errorMessage = extractErrorMessage(err);

                        return {
                            id: memberId,
                            displayName: `Unknown (${memberId})`,
                            entityType: explicitType || data.filterScope,
                            error: true,
                            errorMessage: errorMessage
                        };
                    }
                });

                entities = await Promise.all(memberPromises);

            } else if (data.type === 'enumerated') {
                // For dynamic sets, use the filter to get a sample
                const page = 1;
                const pageSize = 10; // Limit to 10 for preview
                const filter = data.filter && data.filter.trim() ? data.filter : undefined;


                const response = await entityService.getEntitiesByType(data.filterScope, page, pageSize, undefined, filter);
                entities = response.data || [];
                setPreviewTotalResults(response.totalResults || null);
            } else {
                // Unknown type - should not happen
                console.error('Unknown set type:', data.type);
                setPreviewError(`Unknown set type: ${data.type}`);
                setPreviewLoading(false);
                return;
            }

            setPreviewData(entities);
            // Reset total results for manual sets
            if (data.type === 'manual') {
                setPreviewTotalResults(entities.length);
            }
        } catch (err: any) {
            console.error('Error previewing members:', err);
            const errorMessage = extractErrorMessage(err);
            setPreviewError(errorMessage);
        } finally {
            setPreviewLoading(false);
        }
    };

    return (
        <Box>
            {data.isSystem && (
                <Alert
                    icon={<IconInfoCircle size={18} />}
                    color="blue"
                    variant="light"
                    mb="md"
                >
                    This is a system-managed set. Some fields are maintained automatically and changes may be overridden.
                </Alert>
            )}
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {/* Basic Information */}
                <Box style={{ gridColumn: '1 / -1' }}>
                    <Title order={6} mb="xs">
                        Basic Information
                    </Title>
                </Box>

                <Box>
                    <TextInput
                        label="Set Name"
                        placeholder="Name for the set"
                        value={data.displayName}
                        onChange={(e) => handleFieldChange('displayName', e.target.value)}
                        onBlur={(e) => {
                            const trimmed = e.currentTarget.value.trim();
                            if (trimmed !== data.displayName) handleFieldChange('displayName', trimmed);
                        }}
                        disabled={disabled}
                        required
                        description="Name for the set"
                    />
                </Box>

                {showDescription && (
                    <Box>
                        <TextInput
                            label="Description"
                            placeholder="Optional description"
                            value={data.description}
                            onChange={(e) => handleFieldChange('description', e.target.value)}
                            onBlur={(e) => {
                                const trimmed = e.currentTarget.value.trim();
                                if (trimmed !== data.description) handleFieldChange('description', trimmed);
                            }}
                            disabled={disabled}
                            description="Optional description"
                        />
                    </Box>
                )}

                <Box>
                    <Select
                        label="Filter Scope (Entity Type)"
                        placeholder="Select entity type"
                        value={normalizedFilterScope || null}
                        onChange={(value) => handleFieldChange('filterScope', value ?? '')}
                        data={entityTypeOptions}
                        required
                        disabled={disabled}
                    />
                </Box>

                {showSystemToggle && (
                    <Box>
                        <Switch
                            label="System Set"
                            checked={data.isSystem}
                            onChange={(e) => handleFieldChange('isSystem', e.target.checked)}
                            disabled={disabled || true}
                            color="yellow"
                        />
                    </Box>
                )}

                <Box style={{ gridColumn: '1 / -1' }}>
                    <Divider my="md" />
                </Box>

                {/* Set Type Configuration */}
                <Box style={{ gridColumn: '1 / -1' }}>
                    <Title order={6} mb="xs">
                        Set Configuration
                    </Title>
                </Box>

                <Box style={{ gridColumn: '1 / -1' }}>
                    <Switch
                        label={isDynamicMode ? 'Dynamic (Enumerated) Set' : 'Manual Set'}
                        checked={isDynamicMode}
                        onChange={(e) => handleModeChange(e.target.checked)}
                        disabled={disabled}
                    />
                    <Text size="xs" c="dimmed" mt={4} display="block">
                        {isDynamicMode
                            ? 'Set members are determined by a filter expression'
                            : 'Set members are manually specified by ID'
                        }
                    </Text>
                </Box>

                {/* Manual Set Configuration */}
                {!isDynamicMode && (
                    <Box style={{ gridColumn: '1 / -1' }}>
                        <Box mt="md">
                            <Text size="sm" fw={500} mb="xs">
                                Manual Members
                            </Text>
                            <Group gap="xs" mb="md" align="flex-end" wrap="nowrap">
                                <Box flex={1}>
                                <TextInput
                                    label="Add Member ID"
                                    placeholder="Enter an ID and press Enter or click Add"
                                    value={newManualId}
                                    onChange={(e) => setNewManualId(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddManualId();
                                        }
                                    }}
                                    disabled={disabled}
                                    description="Enter an ID and press Enter or click Add"
                                />
                                </Box>
                                <Button
                                    variant="outline"
                                    onClick={handleAddManualId}
                                    disabled={disabled || !newManualId.trim()}
                                    leftSection={<IconPlus size={16} />}
                                >
                                    Add
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => setEntityBrowserOpen(true)}
                                    disabled={disabled || !data.filterScope}
                                    leftSection={<IconSearch size={16} />}
                                >
                                    Browse
                                </Button>
                            </Group>
                            {data.manualMembers.length > 0 && (
                                <Group gap="xs" wrap="wrap">
                                    {data.manualMembers.map((member, index) => {
                                        const id = getMemberId(member);
                                        if (!id) return null;
                                        const displayName = memberDisplayNames[id] || id;
                                        const isLoaded = memberDisplayNames[id] !== undefined;
                                        const badgeLabel = memberNamesLoading && !isLoaded ? `${id}...` : displayName;
                                        const badgeTitle = displayName !== id ? `${displayName} (${id})` : id;
                                        const warning = memberWarnings[id];
                                        const color = warning ? 'yellow' : 'blue';
                                        return (
                                            <Badge
                                                key={`${id}-${index}`}
                                                variant="outline"
                                                color={color}
                                                title={warning ? `${badgeTitle}\n\n${warning}` : badgeTitle}
                                                rightSection={
                                                    !disabled ? (
                                                        <UnstyledButton
                                                            component="span"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveManualId(id);
                                                            }}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <IconX size={12} />
                                                        </UnstyledButton>
                                                    ) : undefined
                                                }
                                            >
                                                {badgeLabel}
                                            </Badge>
                                        );
                                    })}
                                </Group>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Dynamic Set Configuration */}
                {isDynamicMode && (
                    <Box style={{ gridColumn: '1 / -1' }}>
                        <Textarea
                            label="Filter Expression"
                            placeholder="Enter SCIM filter expression..."
                            value={data.filter}
                            onChange={(e) => handleFieldChange('filter', e.target.value)}
                            onBlur={(e) => {
                                const trimmed = e.currentTarget.value.trim();
                                if (trimmed !== data.filter) handleFieldChange('filter', trimmed);
                            }}
                            disabled={disabled}
                            description="SCIM filter expression to determine set members (e.g., 'Role eq Student')"
                            minRows={3}
                        />
                        <Group gap="xs" mt="sm">
                            {showPreviewMembers && (
                                <Button
                                    variant="outline"
                                    leftSection={<IconEye size={16} />}
                                    onClick={handlePreviewMembers}
                                    disabled={disabled || !data.filterScope}
                                    size="xs"
                                >
                                    Preview
                                </Button>
                            )}
                            {!isStandard && (
                                <Button
                                    variant="outline"
                                    size="xs"
                                    onClick={generateRandomFilter}
                                    disabled={disabled || !data.filterScope}
                                    leftSection={<IconDice size={16} />}
                                >
                                    Generate Random
                                </Button>
                            )}
                            {!isStandard && data.filterScope && (
                                <Button
                                    variant="outline"
                                    onClick={loadAvailableAttributes}
                                    disabled={disabled}
                                    size="xs"
                                    leftSection={<IconDatabase size={16} />}
                                >
                                    View Attributes
                                </Button>
                            )}
                        </Group>
                    </Box>
                )}

                {/* Manual Set Preview Button */}
                {!isDynamicMode && showPreviewMembers && (
                    <Box style={{ gridColumn: '1 / -1' }}>
                        <Group justify="flex-end">
                            <Button
                                variant="outline"
                                leftSection={<IconEye size={16} />}
                                onClick={handlePreviewMembers}
                                disabled={disabled || !data.filterScope || data.manualMembers.length === 0}
                            >
                                Preview Members ({data.manualMembers.length})
                            </Button>
                        </Group>
                    </Box>
                )}
            </SimpleGrid>

            {/* Entity Browser Dialog */}
            <EntityBrowserDialog
                open={entityBrowserOpen}
                onClose={() => setEntityBrowserOpen(false)}
                onConfirm={handleEntityBrowserConfirm}
                entityType={data.filterScope}
                title={`Select ${data.filterScope ? getPluralizedLabel(data.filterScope) : 'Entity'} Members`}
                callerName="SetForm"
                alreadySelectedEntities={alreadySelectedEntities}
            />

            {/* Preview Members Modal */}
            <Modal
                opened={previewDialogOpen}
                onClose={() => setPreviewDialogOpen(false)}
                size="lg"
                title={
                    <Group gap="xs">
                        <IconEye size={20} />
                        <Text fw={600}>Preview Set Members</Text>
                    </Group>
                }
            >
                {previewError && (
                    <Alert color="red" mb="md">
                        <Group justify="space-between" wrap="nowrap">
                            <Text size="sm">{previewError}</Text>
                            {data.filterScope && (
                                <Button
                                    variant="subtle"
                                    size="xs"
                                    color="white"
                                    onClick={loadAvailableAttributes}
                                >
                                    View Available Attributes
                                </Button>
                            )}
                        </Group>
                    </Alert>
                )}

                {previewLoading ? (
                    <LoadingState variant="centered" />
                ) : previewData.length === 0 ? (
                    <Text size="sm" c="dimmed" ta="center" py="xl">
                        {data.type === 'manual'
                            ? 'No manual members specified'
                            : 'No entities match the current filter'
                        }
                    </Text>
                ) : (
                    <Paper withBorder>
                        <Table>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>ID</Table.Th>
                                    <Table.Th>Display Name</Table.Th>
                                    <Table.Th>Type</Table.Th>
                                    {data.type === 'manual' && <Table.Th>Status</Table.Th>}
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {previewData.map((entity, index) => (
                                    <Table.Tr key={entity.id || entity.Id || index}>
                                        <Table.Td>
                                            <Text size="xs" ff="monospace">
                                                {entity.id || entity.Id || 'N/A'}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td>
                                            {entity.displayName || entity.DisplayName || entity.userName || entity.UserName || 'N/A'}
                                        </Table.Td>
                                        <Table.Td>
                                            {entity.EntityType || entity.entityType || data.filterScope || 'N/A'}
                                        </Table.Td>
                                        {data.type === 'manual' && (
                                            <Table.Td>
                                                {entity.error ? (
                                                    <Box>
                                                        <Badge size="sm" color="red" variant="filled" mb={4}>
                                                            Error
                                                        </Badge>
                                                        {entity.errorMessage && (
                                                            <Text size="xs" c="red" display="block" maw={200} style={{ wordBreak: 'break-word' }}>
                                                                {entity.errorMessage}
                                                            </Text>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Badge size="sm" color="green" variant="filled">
                                                        Found
                                                    </Badge>
                                                )}
                                            </Table.Td>
                                        )}
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Paper>
                )}

                {previewData.length > 0 && data.type === 'enumerated' && (
                    <Text size="xs" c="dimmed" mt="md" display="block">
                        {previewTotalResults !== null ? (
                            <>
                                Showing <strong>10</strong> of <strong>{previewTotalResults.toLocaleString()}</strong> matching entities.
                                {previewTotalResults > 10 && ' The actual set may contain more members.'}
                            </>
                        ) : (
                            'Showing first 10 entities that match the filter. The actual set may contain more members.'
                        )}
                    </Text>
                )}

                <Group justify="flex-end" mt="md">
                    <Button onClick={() => setPreviewDialogOpen(false)}>
                        Close
                    </Button>
                </Group>
            </Modal>

            {/* Available Attributes Modal */}
            <Modal
                opened={showAttributesDialog}
                onClose={() => setShowAttributesDialog(false)}
                size="lg"
                title={`Available Attributes for ${data.filterScope}`}
            >
                <Text size="sm" c="dimmed" mb="md">
                    These are the attributes available for filtering {data.filterScope} entities:
                </Text>
                <Paper withBorder style={{ maxHeight: 400, overflow: 'auto' }}>
                    <Table>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th><strong>Attribute Name</strong></Table.Th>
                                <Table.Th><strong>Type</strong></Table.Th>
                                <Table.Th><strong>Required</strong></Table.Th>
                                <Table.Th><strong>Multi-valued</strong></Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {availableAttributes.map((attr, index) => (
                                <Table.Tr key={`${attr.name || attr.id}-${index}`}>
                                    <Table.Td>
                                        <Text size="sm" ff="monospace">
                                            {attr.name || attr.id}
                                        </Text>
                                    </Table.Td>
                                    <Table.Td>
                                        <Badge size="sm" variant="outline">
                                            {attr.type || 'string'}
                                        </Badge>
                                    </Table.Td>
                                    <Table.Td>
                                        <Badge
                                            size="sm"
                                            color={attr.required ? 'red' : 'gray'}
                                            variant="outline"
                                        >
                                            {attr.required ? 'Yes' : 'No'}
                                        </Badge>
                                    </Table.Td>
                                    <Table.Td>
                                        <Badge
                                            size="sm"
                                            color={attr.multiValued ? 'yellow' : 'gray'}
                                            variant="outline"
                                        >
                                            {attr.multiValued ? 'Yes' : 'No'}
                                        </Badge>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Paper>
                <Group justify="flex-end" mt="md">
                    <Button onClick={() => setShowAttributesDialog(false)}>
                        Close
                    </Button>
                </Group>
            </Modal>
        </Box>
    );
};

export default SetForm;
